import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Trash2, Upload, Image as ImageIcon, Video, Save, Sparkles } from "lucide-react";
import Button from "../ui/Button";
import Badge from "../ui/Badge";

/**
 * Full product editor used by admins from AdminDashboard.
 *
 * Modes:
 *   - `product` prop is an object → edit that product (PUT /admin/products/{id})
 *   - `product` prop is null → create a new product (POST /admin/products)
 *
 * Sections:
 *   Base (name, description, category, subscription flag, version)
 *   Pricing (base_price + discount; live final price preview with base struck through)
 *   Features (chip list)
 *   Requirements (bullet list)
 *   Changelog / Dettagli (bullet list)
 *   Media (image URLs + video URLs; file upload for both)
 */
export default function ProductEditor({ product, onClose, onSaved }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const isEdit = !!product?.product_id;
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null); // 'image' | 'video' | null

  const [form, setForm] = useState(() => ({
    name: product?.name || "",
    description: product?.description || "",
    lookup_key: product?.lookup_key || "",
    category: product?.category || "software",
    subscription: !!product?.subscription,
    version: product?.version || "1.0.0",
    base_price: product?.base_price ?? 0,
    discount_percentage: product?.discount_percentage ?? 0,
    discount_amount: product?.discount_amount ?? 0,
    discount_active: !!product?.discount_active,
    features: product?.features || [],
    requirements: product?.requirements || [],
    changelog: product?.changelog || [],
    images: product?.images || [],
    videos: product?.videos || [],
    active: product?.active === undefined ? true : !!product.active,
  }));

  const finalPrice = useMemo(() => {
    const base = parseFloat(form.base_price) || 0;
    if (!form.discount_active) return base;
    const pct = parseFloat(form.discount_percentage) || 0;
    const amt = parseFloat(form.discount_amount) || 0;
    let p = pct ? base * (1 - pct / 100) : base;
    p = amt ? p - amt : p;
    return Math.max(Math.round(p * 100) / 100, 0);
  }, [form.base_price, form.discount_percentage, form.discount_amount, form.discount_active]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.description) {
      alert("Nome e descrizione sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/api/admin/products/${product.product_id}`
        : `${API}/api/admin/products`;
      const method = isEdit ? "PUT" : "POST";
      const resp = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      onSaved?.(data.product);
      onClose?.();
    } catch (err) {
      alert("Errore salvataggio: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-background/85 backdrop-blur-sm"
      data-testid="product-editor"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="rune-border bg-card rounded-lg w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-gold" />
            <div>
              <h3 className="font-display text-2xl text-gold">
                {isEdit ? `Modifica: ${product?.name}` : "Nuovo Prodotto"}
              </h3>
              <p className="font-serif-fantasy italic text-sm text-muted-foreground">
                Tutti i campi visibili nello Store possono essere modificati qui.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 transition-all"
            data-testid="product-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto scrollbar-thin flex-1 space-y-6">
          {/* BASE */}
          <Section title="Informazioni Base">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome *">
                <input
                  value={form.name}
                  onChange={(e) => patch("name", e.target.value)}
                  className="input"
                  data-testid="pe-name"
                />
              </Field>
              <Field label="Chiave (slug URL)">
                <input
                  value={form.lookup_key}
                  onChange={(e) => patch("lookup_key", e.target.value)}
                  placeholder="lasciata vuota = auto"
                  className="input"
                  data-testid="pe-lookup-key"
                />
              </Field>
              <Field label="Categoria">
                <input value={form.category} onChange={(e) => patch("category", e.target.value)} className="input" data-testid="pe-category" />
              </Field>
              <Field label="Versione">
                <input value={form.version} onChange={(e) => patch("version", e.target.value)} className="input" data-testid="pe-version" />
              </Field>
              <Field label="Tipo">
                <div className="flex gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm text-parchment cursor-pointer">
                    <input type="radio" checked={!form.subscription} onChange={() => patch("subscription", false)} data-testid="pe-type-one" />
                    Una Tantum
                  </label>
                  <label className="flex items-center gap-2 text-sm text-parchment cursor-pointer">
                    <input type="radio" checked={form.subscription} onChange={() => patch("subscription", true)} data-testid="pe-type-sub" />
                    Abbonamento
                  </label>
                </div>
              </Field>
              <Field label="Stato">
                <label className="flex items-center gap-2 text-sm text-parchment pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => patch("active", e.target.checked)}
                    data-testid="pe-active"
                  />
                  Prodotto Attivo (visibile nello Store)
                </label>
              </Field>
            </div>
            <Field label="Descrizione *">
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => patch("description", e.target.value)}
                className="input"
                data-testid="pe-description"
              />
            </Field>
          </Section>

          {/* PRICING */}
          <Section title="Prezzi e Sconti">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Prezzo Base (€)">
                <input
                  type="number" step="0.01"
                  value={form.base_price}
                  onChange={(e) => patch("base_price", parseFloat(e.target.value) || 0)}
                  className="input"
                  data-testid="pe-base-price"
                />
              </Field>
              <Field label="Sconto Percentuale (%)">
                <input
                  type="number" step="1"
                  value={form.discount_percentage}
                  onChange={(e) => patch("discount_percentage", parseFloat(e.target.value) || 0)}
                  className="input"
                  data-testid="pe-discount-pct"
                />
              </Field>
              <Field label="Sconto Fisso (€)">
                <input
                  type="number" step="0.01"
                  value={form.discount_amount}
                  onChange={(e) => patch("discount_amount", parseFloat(e.target.value) || 0)}
                  className="input"
                  data-testid="pe-discount-amt"
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-parchment cursor-pointer">
              <input
                type="checkbox"
                checked={form.discount_active}
                onChange={(e) => patch("discount_active", e.target.checked)}
                data-testid="pe-discount-active"
              />
              Attiva Sconto
            </label>
            <div className="rune-border rounded-md p-3 bg-card/40 flex items-baseline gap-3" data-testid="pe-price-preview">
              <span className="font-heading text-[10px] tracking-widest text-muted-foreground uppercase">Anteprima:</span>
              {form.discount_active ? (
                <>
                  <span className="text-ruby line-through font-heading text-lg" data-testid="pe-preview-base">€{Number(form.base_price).toFixed(2)}</span>
                  <span className="text-gold font-display text-2xl" data-testid="pe-preview-final">€{finalPrice.toFixed(2)}</span>
                </>
              ) : (
                <span className="text-gold font-display text-2xl" data-testid="pe-preview-final">€{Number(form.base_price).toFixed(2)}</span>
              )}
            </div>
          </Section>

          {/* FEATURES / REQUIREMENTS / CHANGELOG */}
          <StringListEditor
            title="Funzionalità"
            items={form.features}
            onChange={(v) => patch("features", v)}
            placeholder="es. Editor mappe con drag e zoom"
            testPrefix="pe-features"
          />
          <StringListEditor
            title="Requisiti"
            items={form.requirements}
            onChange={(v) => patch("requirements", v)}
            placeholder="es. Browser moderno (Chrome, Firefox, Safari)"
            testPrefix="pe-requirements"
          />
          <StringListEditor
            title="Dettagli / Changelog"
            items={form.changelog}
            onChange={(v) => patch("changelog", v)}
            placeholder="es. v1.2 · Aggiunta gestione clima marino"
            testPrefix="pe-changelog"
          />

          {/* MEDIA */}
          <MediaListEditor
            title="Immagini"
            kind="image"
            items={form.images}
            onChange={(v) => patch("images", v)}
            productId={product?.product_id}
            api={API}
            uploading={uploadingKind === "image"}
            setUploading={(v) => setUploadingKind(v ? "image" : null)}
            disabledUpload={!isEdit}
          />
          <MediaListEditor
            title="Video"
            kind="video"
            items={form.videos}
            onChange={(v) => patch("videos", v)}
            productId={product?.product_id}
            api={API}
            uploading={uploadingKind === "video"}
            setUploading={(v) => setUploadingKind(v ? "video" : null)}
            disabledUpload={!isEdit}
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} data-testid="pe-cancel">
            Annulla
          </Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving} data-testid="pe-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio…" : isEdit ? "Salva Modifiche" : "Crea Prodotto"}
          </Button>
        </div>
      </div>

      {/* Shared input styling */}
      <style>{`
        .input { width:100%; background: hsl(var(--input, 220 20% 12%)); color: var(--foreground);
          border: 1px solid rgba(212,175,55,0.25); border-radius: 6px; padding: 8px 12px;
          font-size: 14px; outline: none; transition: border-color 120ms; }
        .input:focus { border-color: rgb(212,175,55); }
      `}</style>
    </div>
  );
}

/* -------- Reusable subcomponents -------- */

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="font-heading text-[10px] tracking-widest text-gold uppercase">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-heading tracking-widest text-muted-foreground uppercase mb-1">{label}</label>
      {children}
    </div>
  );
}

/* -------- String list editor (features / requirements / changelog) -------- */

function StringListEditor({ title, items, onChange, placeholder, testPrefix }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const update = (idx, v) => onChange(items.map((it, i) => (i === idx ? v : it)));

  return (
    <Section title={title}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`${testPrefix}-row-${i}`}>
            <input
              value={it}
              onChange={(e) => update(i, e.target.value)}
              className="input flex-1"
              data-testid={`${testPrefix}-input-${i}`}
            />
            <button
              onClick={() => remove(i)}
              className="p-1.5 rounded text-ruby/70 hover:text-ruby hover:bg-ruby/10 transition-colors"
              title="Rimuovi"
              data-testid={`${testPrefix}-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="input flex-1"
          data-testid={`${testPrefix}-draft`}
        />
        <Button variant="secondary" size="sm" onClick={add} data-testid={`${testPrefix}-add`}>
          <Plus className="w-4 h-4 mr-1" /> Aggiungi
        </Button>
      </div>
    </Section>
  );
}

/* -------- Media list editor (images / videos) -------- */

function MediaListEditor({ title, kind, items, onChange, productId, api, uploading, setUploading, disabledUpload }) {
  const [draft, setDraft] = useState("");
  const fileRef = useRef(null);

  const addUrl = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!productId) {
      alert("Prima salva il prodotto (Crea Prodotto), poi potrai caricare i file.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${api}/api/admin/products/${productId}/media`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      onChange([...(items || []), data.url]);
    } catch (err) {
      alert("Upload fallito: " + err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewUrl = (u) => (u.startsWith("http") || u.startsWith("/") ? u : u);

  return (
    <Section title={title}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(items || []).map((url, i) => (
          <div key={i} className="relative rune-border rounded-md overflow-hidden bg-card/40 group" data-testid={`pe-${kind}-row-${i}`}>
            {kind === "image" ? (
              <img src={previewUrl(url)} alt="" className="w-full h-32 object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
            ) : (
              <video src={previewUrl(url)} className="w-full h-32 object-cover bg-black" muted controls={false} />
            )}
            <div className="absolute inset-x-0 bottom-0 bg-black/70 backdrop-blur-sm p-1 flex items-center justify-between">
              <span className="text-[10px] text-parchment truncate flex-1 px-1">{url.split("/").pop()}</span>
              <button
                onClick={() => remove(i)}
                className="p-1 rounded text-ruby hover:bg-ruby/20"
                data-testid={`pe-${kind}-remove-${i}`}
                title="Rimuovi"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex gap-2 flex-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Incolla URL ${kind === "image" ? "immagine" : "video"}…`}
            className="input flex-1"
            data-testid={`pe-${kind}-url-input`}
          />
          <Button variant="secondary" size="sm" onClick={addUrl} data-testid={`pe-${kind}-url-add`}>
            <Plus className="w-4 h-4 mr-1" /> URL
          </Button>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={kind === "image" ? "image/*" : "video/*"}
            onChange={uploadFile}
            className="hidden"
            data-testid={`pe-${kind}-file-input`}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={uploading || disabledUpload}
            onClick={() => fileRef.current?.click()}
            data-testid={`pe-${kind}-upload-btn`}
            title={disabledUpload ? "Salva prima il prodotto per abilitare l'upload" : ""}
          >
            {kind === "image" ? <ImageIcon className="w-4 h-4 mr-1" /> : <Video className="w-4 h-4 mr-1" />}
            {uploading ? "Caricamento…" : `Carica ${kind === "image" ? "Immagine" : "Video"}`}
          </Button>
        </div>
      </div>
      {disabledUpload && (
        <p className="text-[10px] text-muted-foreground italic">
          L'upload di file è disponibile dopo la creazione del prodotto. Puoi comunque aggiungere URL già esistenti.
        </p>
      )}
    </Section>
  );
}
